import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useWaehrungen } from './useWaehrungen'
import { WaehrungsWahl } from '../pages/priceFormat'

/**
 * Die Zielwährung einer Ansicht — gemerkt, bis der Tab zugeht.
 *
 * SIE GILT FÜR DIE SITZUNG, nicht für die Seite. Vorher stand sie nur in der
 * Adresse: Wer sie auf den Kursen wählte und dann zu den Handelsideen ging,
 * fand dort wieder „—" und musste erneut umschalten. Bei sechs Ansichten, die
 * sie tragen, ist das sechsmal dieselbe Handbewegung.
 *
 * DIE ADRESSE GEWINNT TROTZDEM. Ein Link mit `?currency=USD` zeigt Dollar,
 * auch wenn im Speicher Euro steht — sonst könnte man keine Ansicht mehr
 * weitergeben, ohne dass der Empfänger etwas anderes sieht als der Absender.
 * Nur wenn die Adresse schweigt, greift das Gemerkte.
 *
 * `sessionStorage` UND NICHT `localStorage`: Die Wahl lebt, solange der Tab
 * offen ist. Ein neuer Tag beginnt wieder in der Handelswährung — das ist die
 * Währung, in der die Kurse tatsächlich notieren, und damit der ehrlichere
 * Ausgangszustand.
 */
const SCHLUESSEL = 'iw.zielwaehrung'

/**
 * Lesen und Schreiben in `try`: `sessionStorage` WIRFT, wenn der Browser
 * Website-Daten blockiert — im privaten Fenster mancher Browser schon beim
 * blossen Zugriff. Eine vergessene Zielwährung ist ein kleiner Verlust, eine
 * weisse Seite wäre ein grosser.
 */
function gemerkte(): string {
  try {
    return sessionStorage.getItem(SCHLUESSEL) ?? ''
  } catch {
    return ''
  }
}

function merken(wert: string): void {
  try {
    if (wert) sessionStorage.setItem(SCHLUESSEL, wert)
    // Abwählen heisst LÖSCHEN und nicht "leer speichern": Sonst setzte die
    // Wiederherstellung unten die alte Wahl sofort zurück, und „—" liesse
    // sich nie auswählen.
    else sessionStorage.removeItem(SCHLUESSEL)
  } catch {
    /* Ohne Speicher gilt die Wahl eben nur für diese Ansicht. */
  }
}

export function useZielwaehrung(erlaubt: boolean): {
  waehrung: string
  /** Das fertige Bedienelement — gehört in `usePageActions`. */
  wahl: ReactNode
} {
  const [suche, setSuche] = useSearchParams()
  const waehrungen = useWaehrungen(erlaubt)

  // Der gemerkte Wert liegt im Zustand und wird EINMAL gelesen. Im Zustand,
  // weil das Abwählen ihn sofort verwerfen muss; einmal, weil ein Lesen bei
  // jedem Rendern nichts hinzufügt — schreiben tut nur diese Komponente.
  const [gemerkt, setGemerkt] = useState(gemerkte)

  // SCHON BEIM ERSTEN RENDERN die richtige Währung, nicht erst nach einem
  // Effekt: Die Ladefunktionen hängen an diesem Wert. Käme er eine Runde
  // später, holte jede Ansicht ihre Daten zweimal — einmal ohne Umrechnung
  // und gleich darauf noch einmal mit.
  const waehrung = suche.get('currency') ?? gemerkt

  const waehlen = useCallback((gewaehlt: string) => {
    merken(gewaehlt)
    setGemerkt(gewaehlt)
    const naechste = new URLSearchParams(suche)
    if (gewaehlt) naechste.set('currency', gewaehlt)
    else naechste.delete('currency')
    // `replace`: Eine Zielwährung ist eine Anzeigeeinstellung, kein Schritt.
    // Mit einem Verlaufseintrag müsste die Zurück-Taste sie durchlaufen,
    // bevor sie zur vorigen Ansicht käme.
    setSuche(naechste, { replace: true })
  }, [suche, setSuche])

  // Das Gemerkte in die Adresse nachtragen, damit ein weitergegebener Link
  // dasselbe zeigt. Ändert nur die Adresse, nicht `waehrung` — es wird also
  // nichts neu geladen.
  useEffect(() => {
    if (suche.get('currency') || !gemerkt) return
    const naechste = new URLSearchParams(suche)
    naechste.set('currency', gemerkt)
    setSuche(naechste, { replace: true })
  }, [suche, gemerkt, setSuche])

  // `useMemo`, weil `usePageActions` das Element als Abhängigkeit führt: Ein
  // bei jedem Rendern neu erzeugtes JSX setzte den Zustand endlos neu.
  const wahl = useMemo(() => (
    <WaehrungsWahl waehrungen={waehrungen} wert={waehrung} setzen={waehlen} />
  ), [waehrungen, waehrung, waehlen])

  return { waehrung, wahl }
}
